import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { Badge } from "~/components/ui/badge";
import { cn } from "~/lib/utils";
import { GitCompareArrows, Loader2 } from "lucide-react";
import { useState } from "react";
import type { PolicyFormData } from "./policy-form";

interface Policy {
  name: string;
  command: string;
  permissive: string;
  using_expression: string | null;
  with_check_expression: string | null;
  roles: string[] | string;
}

function formatRoles(r: string[] | string | undefined): string {
  if (!r) return "public";
  if (Array.isArray(r)) return r.join(", ");
  if (typeof r === "string" && r.startsWith("{") && r.endsWith("}")) {
    return r.slice(1, -1).split(",").join(", ");
  }
  return r || "public";
}

function DiffRow({
  label,
  oldValue,
  newValue,
}: {
  label: string;
  oldValue: string | null;
  newValue: string | null;
}) {
  const oldDisplay = oldValue || "(none)";
  const newDisplay = newValue || "(none)";
  const changed = oldValue !== newValue;

  return (
    <div className="grid grid-cols-[120px_1fr_1fr] gap-2 text-xs">
      <div className="font-medium text-muted-foreground uppercase tracking-wider py-1.5">
        {label}
      </div>
      <div
        className={cn(
          "rounded px-2 py-1.5 font-mono break-all",
          changed && oldValue
            ? "bg-red-500/10 text-red-600 dark:text-red-400"
            : "bg-secondary/50 text-muted-foreground",
        )}
      >
        {oldDisplay}
      </div>
      <div
        className={cn(
          "rounded px-2 py-1.5 font-mono break-all",
          changed && newValue
            ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
            : "bg-secondary/50 text-muted-foreground",
        )}
      >
        {newDisplay}
      </div>
    </div>
  );
}

export function PolicyDiffDialog({
  open,
  onOpenChange,
  currentPolicy,
  proposedPolicy,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPolicy: Policy | null;
  proposedPolicy: PolicyFormData;
  onConfirm: () => Promise<void>;
}) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isNew = !currentPolicy;
  const oldName = currentPolicy?.name ?? null;
  const newName = proposedPolicy.name;

  const oldCommands = currentPolicy?.command ?? null;
  const newCommands =
    proposedPolicy.commands?.length > 0
      ? proposedPolicy.commands.join(", ")
      : proposedPolicy.command;

  const oldPermissive = currentPolicy?.permissive ?? null;
  const newPermissive = proposedPolicy.permissive ? "PERMISSIVE" : "RESTRICTIVE";

  const oldUsing = currentPolicy?.using_expression ?? null;
  const newUsing = proposedPolicy.using || null;

  const oldCheck = currentPolicy?.with_check_expression ?? null;
  const newCheck = proposedPolicy.withCheck || null;

  const oldRoles = currentPolicy ? formatRoles(currentPolicy.roles) : null;
  const newRoles = proposedPolicy.roles || "public";

  async function handleConfirm() {
    setConfirming(true);
    setError(null);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (err) {
      // Without this the rejection was unhandled: the dialog reported nothing
      // and the operator was left believing a policy had been created that
      // Postgres had in fact refused. A silently absent security policy is the
      // worst possible failure for this dialog, so the error stays on screen
      // and the dialog stays open with the definition intact to correct.
      setError(err instanceof Error ? err.message : "Could not apply the policy");
    } finally {
      setConfirming(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompareArrows className="h-4 w-4" />
            {isNew ? "Review New Policy" : "Review Policy Changes"}
          </DialogTitle>
          <DialogDescription>
            {isNew
              ? "Review the policy definition before creating it."
              : "Compare the current and proposed policy side by side."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-1">
          {/* Column headers */}
          <div className="grid grid-cols-[120px_1fr_1fr] gap-2 text-xs mb-2">
            <div />
            <div className="font-medium text-muted-foreground px-2">Current</div>
            <div className="font-medium text-muted-foreground px-2">Proposed</div>
          </div>

          <DiffRow label="Name" oldValue={oldName} newValue={newName} />
          <DiffRow label="Commands" oldValue={oldCommands} newValue={newCommands} />
          <DiffRow
            label="Type"
            oldValue={oldPermissive}
            newValue={newPermissive}
          />
          <DiffRow label="Roles" oldValue={oldRoles} newValue={newRoles} />
          <DiffRow label="USING" oldValue={oldUsing} newValue={newUsing} />
          <DiffRow label="WITH CHECK" oldValue={oldCheck} newValue={newCheck} />

          {/* Summary badges */}
          <div className="flex items-center gap-2 pt-3 border-t border-border mt-3">
            {isNew ? (
              <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 text-xs">
                New policy
              </Badge>
            ) : (
              <Badge className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 text-xs">
                Update existing policy
              </Badge>
            )}
          </div>
        </div>

        {error && (
          <p role="alert" className="text-[13px] text-destructive">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={confirming}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={confirming}>
            {confirming && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
